import {
  AlphaQuoteLineStatus,
  type FieldDefinition,
  type LineItem,
  type LineItemField,
  type LineItemFieldId,
  type LineItemFieldKey,
  lineItemFieldModel,
  LineItemValueId,
} from '@snap-types/models/auto-configurator/state';
import { UnitOfMeasure } from '@snap-types/models/auto-configurator/state';
import {
  AlphaDimensionUnit,
  AlphaQuoteEvaluationStatus,
  AlphaQuoteSyncStatus,
} from '@snap-types/models/enums';
import { generateUUID } from '@ui/util/uuid';

import {
  buildBooleanValue,
  buildDecimalValue,
  buildDescriptionValue,
  buildNumberValue,
  buildSelectValue,
  buildTextValue,
} from './value';

export function buildLineItemFieldId(key: LineItemFieldKey): LineItemFieldId {
  return {
    model: lineItemFieldModel,
    key,
  };
}

export function buildLineItemValueId(
  key: LineItemFieldKey,
  uuid: string,
): LineItemValueId {
  return {
    model: lineItemFieldModel,
    key,
    uuid,
  };
}

export function buildLineItemField(
  key: LineItemFieldKey,
  definition: FieldDefinition,
): LineItemField {
  return {
    model: lineItemFieldModel,
    key,
    definition,
  };
}

export function buildLineItem(opts: Partial<LineItem> = {}): LineItem {
  return {
    uuid: generateUUID(),

    isPending: false,
    status: AlphaQuoteLineStatus.ACTIVE,
    evaluationStatus: AlphaQuoteEvaluationStatus.CALCULATED,
    syncStatus: AlphaQuoteSyncStatus.UNSYNCED,
    snapQuoteLineUuid: null,
    lastAppliedAt: null,
    lastApplyFailedAt: null,
    calculatedData: null,
    attachments: [],
    quantityBreakUuids: [],

    lineNumber: buildNumberValue(1),
    description: buildDescriptionValue(),
    descriptionIsOverridden: buildBooleanValue(false),

    dimensionUnit: AlphaDimensionUnit.IMPERIAL,
    drawingId: buildTextValue(),
    drawingRevisionNo: buildTextValue(),
    notes: buildTextValue(),
    customerPartId: buildTextValue(),
    fastenerType: buildSelectValue(),
    material: buildSelectValue(),
    dfars: buildBooleanValue(false),
    threadSize: buildSelectValue(),
    threadPitch: buildSelectValue(),
    length: buildDecimalValue(),
    shoulderDiameter: buildDecimalValue(),
    shoulderLength: buildDecimalValue(),
    headStyle: buildSelectValue(),
    recess: buildSelectValue(),
    serration: buildBooleanValue(false),
    surfaceFinish: buildSelectValue(),
    postFinish: buildSelectValue(),

    // SEMS 1
    sems1Style: buildSelectValue(),
    sems1Weight: buildDecimalValue(),

    // SEMS 2
    sems2Style: buildSelectValue(),
    sems2Weight: buildDecimalValue(),

    // OSP 2
    osp2: buildSelectValue(),
    osp2Description: buildTextValue(),
    osp2UOM: buildSelectValue({
      label: UnitOfMeasure.EA,
      value: UnitOfMeasure.EA,
    }),
    osp2MinLot: buildDecimalValue(),

    // OSP 3
    osp3: buildSelectValue(),
    osp3Description: buildTextValue(),
    osp3UOM: buildSelectValue({
      label: UnitOfMeasure.EA,
      value: UnitOfMeasure.EA,
    }),
    osp3MinLot: buildDecimalValue(),

    // Internal operation notes
    toolingNote: buildTextValue(),
    headingNote: buildTextValue(),
    trimmingNote: buildTextValue(),
    pinchNote: buildTextValue(),
    rollingNote: buildTextValue(),
    shankSlottingNote: buildTextValue(),
    sawSlottingNote: buildTextValue(),
    dephosNote: buildTextValue(),
    heatTreatNote: buildTextValue(),
    surfaceFinishNote: buildTextValue(),
    postFinishNote: buildTextValue(),
    osp2Note: buildTextValue(),
    osp3Note: buildTextValue(),

    hasSemsEmbed: buildBooleanValue(false),

    ...opts,
  };
}
